/**
 * EntityMenu
 * Classification: custom (composes MUI Menu)
 *
 * The detail and pivot menu opened by `Chip purpose="entity"`. Three regions in
 * a fixed order -- identity, attributes, actions -- so analysts build muscle
 * memory across every entity they open. The shape follows Carbon's
 * explainability popover template: what this is, then the supporting detail,
 * then what you can do about it.
 *
 * The copy actions live here rather than at the call site because they are the
 * reason the pattern exists, and because getting them right is not obvious: a
 * copy must yield the canonical value even when the chip shows a truncated one,
 * and a defanged copy must be correct for the kind.
 */
import { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Snackbar from '@mui/material/Snackbar';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import {
  ENTITY_KIND_LABEL,
  defangIndicator,
  isDefangable,
  type EntityKind,
  type EntityVerdict,
} from '@/atoms/Chip';
import { fontFamily } from '@/tokens/typography';

/** A single supporting fact about the entity. */
export interface EntityAttribute {
  label: string;
  value: string;
}

export interface Entity {
  kind: EntityKind;
  /** Canonical, full value. Copy actions always yield this, never `display`. */
  value: string;
  /** Shortened text the chip shows. Never what gets copied. */
  display?: string;
  /** Omit when the value was never resolved. */
  verdict?: EntityVerdict;
  attributes?: EntityAttribute[];
}

export interface EntityMenuAction {
  id: string;
  label: string;
  icon?: React.ReactNode;
  /**
   * Why this action cannot be run, if it cannot. Rendered as text beneath the
   * label: a greyed row with no explanation reads as a bug and gets retried.
   */
  unavailableReason?: string;
}

export interface EntityMenuProps {
  entity: Entity;
  /** The chip that opened the menu. */
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /**
   * Pivot and navigation actions, appended after the copy actions. Keep to
   * three or so: this menu opens from a word in a paragraph, so it is far too
   * easy to trigger by accident for anything destructive or privileged. Those
   * belong on the entity page behind confirmation and permission checks.
   */
  actions?: EntityMenuAction[];
  onAction?: (actionId: string, entity: Entity) => void;
  /** Suppresses the copy confirmation, for callers with their own Snackbar. */
  disableCopyFeedback?: boolean;
}

/**
 * Writes to the clipboard, falling back to a hidden textarea where the async
 * Clipboard API is unavailable -- it needs a secure context, so an analyst on a
 * plain-HTTP internal deployment would otherwise get a silent no-op.
 */
async function writeToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path rather than reporting failure: a rejected
    // permission prompt still leaves execCommand viable in some browsers.
  }

  try {
    const scratch = document.createElement('textarea');
    scratch.value = text;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.opacity = '0';
    document.body.appendChild(scratch);
    scratch.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(scratch);
    return ok;
  } catch {
    return false;
  }
}

/**
 * @example
 * const [anchor, setAnchor] = useState<HTMLElement | null>(null);
 *
 * <Chip purpose="entity" kind="ip" value="203.0.113.47" onOpen={(e) => setAnchor(e.currentTarget)} />
 * <EntityMenu
 *   entity={{ kind: 'ip', value: '203.0.113.47', verdict: 'malicious' }}
 *   anchorEl={anchor}
 *   open={Boolean(anchor)}
 *   onClose={() => setAnchor(null)}
 *   actions={[{ id: 'pivot', label: 'Pivot to search' }]}
 *   onAction={handleAction}
 * />
 */
export function EntityMenu({
  entity,
  anchorEl,
  open,
  onClose,
  actions = [],
  onAction,
  disableCopyFeedback = false,
}: EntityMenuProps) {
  const [toast, setToast] = useState<string | null>(null);

  const kindLabel = ENTITY_KIND_LABEL[entity.kind];
  // Boolean, not the raw length: an empty attributes array would otherwise
  // render a literal "0" into the menu.
  const hasAttributes = Boolean(entity.verdict) || Boolean(entity.attributes?.length);

  const handleCopy = async (text: string, what: string) => {
    const ok = await writeToClipboard(text);
    onClose();
    if (!disableCopyFeedback) {
      setToast(ok ? `${what} copied` : `Could not copy ${what.toLowerCase()}`);
    }
  };

  const handleAction = (action: EntityMenuAction) => {
    if (action.unavailableReason) return;
    onClose();
    onAction?.(action.id, entity);
  };

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: { sx: { minWidth: 260, maxWidth: 360 } },
          // The menu is named by the entity so a screen-reader user knows which
          // of several chips in a paragraph they have opened.
          list: { 'aria-label': `${kindLabel} ${entity.value} actions`, dense: true },
        }}
      >
        {/* Identity */}
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {kindLabel}
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: fontFamily.mono, wordBreak: 'break-all', color: 'text.primary' }}
          >
            {entity.value}
          </Typography>
        </Box>

        {/* Attributes */}
        {hasAttributes && <Divider />}
        {hasAttributes && (
          <Box sx={{ px: 2, py: 1, display: 'grid', gap: 0.5 }}>
            {entity.verdict && <AttributeRow label="Verdict" value={capitalise(entity.verdict)} />}
            {entity.attributes?.map((attribute) => (
              <AttributeRow key={attribute.label} {...attribute} />
            ))}
          </Box>
        )}

        <Divider />

        {/* Actions */}
        <MenuItem onClick={() => handleCopy(entity.value, 'Value')}>
          <ListItemIcon>
            <ContentCopyIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Copy value" />
        </MenuItem>

        {isDefangable(entity.kind) && (
          <MenuItem onClick={() => handleCopy(defangIndicator(entity.value), 'Defanged value')}>
            <ListItemIcon>
              <ShieldOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary="Copy defanged"
              secondary={defangIndicator(entity.value)}
              slotProps={{
                secondary: { sx: { fontFamily: fontFamily.mono, wordBreak: 'break-all' } },
              }}
            />
          </MenuItem>
        )}

        {actions.map((action) => (
          <MenuItem
            key={action.id}
            onClick={() => handleAction(action)}
            disabled={Boolean(action.unavailableReason)}
            // A disabled MenuItem is removed from the tab order, which hides the
            // reason from keyboard and screen-reader users. Keeping it focusable
            // is what lets them find out why.
            sx={{ '&.Mui-disabled': { pointerEvents: 'auto' } }}
          >
            {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
            <ListItemText primary={action.label} secondary={action.unavailableReason} />
          </MenuItem>
        ))}
      </Menu>

      <Snackbar
        open={Boolean(toast)}
        message={toast ?? ''}
        autoHideDuration={2500}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}

function AttributeRow({ label, value }: EntityAttribute) {
  return (
    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'space-between' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.primary', textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  );
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export default EntityMenu;
